/**
 * NotesPanel — chalkboard workspace panel (v1 scaffold).
 *
 * Future bridge (NOT wired yet): chart context menu "Send to Notes" should push
 * typed objects into this document using the same union as chart drawings
 * (trendline / sr via SRLineSettingsMenu + useTrendlines, plus chalkCandle /
 * chalkFootprint stamps). Keep object shapes compatible; do not import
 * FootprintChartCanvas here.
 *
 * Document model (localStorage key cheddar_notes_v1):
 *   { version: 1, pages: [{ id, objects: StrokeObject[] }], activePage }
 *
 * StrokeObject union (extensible):
 *   { type: 'pen'|'line'|'rect'|'text'|'chalkCandle'|'chalkFootprint'|'trendline'|'sr', ... }
 */
import { useState, useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from 'react'

const STORAGE_KEY = 'cheddar_notes_v1'
const CHALK = {
  white: '#e8e4da',
  orange: '#e0893a',
  blue: '#5b8fd9',
  red: '#d45b5b',
  board: '#0a0a0a',
}

const TOOLS = [
  { id: 'select', label: 'Select' },
  { id: 'pen', label: 'Pen' },
  { id: 'line', label: 'Line' },
  { id: 'rect', label: 'Rect' },
  { id: 'text', label: 'Text' },
]

const COLORS = [
  { id: 'white', hex: CHALK.white },
  { id: 'orange', hex: CHALK.orange },
  { id: 'blue', hex: CHALK.blue },
  { id: 'red', hex: CHALK.red },
]

function uid(prefix = 'o') {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
}

function emptyPage() {
  return { id: uid('page'), objects: [] }
}

function defaultDoc() {
  const page = emptyPage()
  return { version: 1, pages: [page], activePage: page.id }
}

function loadDoc() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return defaultDoc()
    const parsed = JSON.parse(raw)
    if (!parsed || parsed.version !== 1 || !Array.isArray(parsed.pages) || !parsed.pages.length) {
      return defaultDoc()
    }
    // Migrate legacy `strokes` → `objects` if ever present
    parsed.pages = parsed.pages.map((p) => ({
      id: p.id || uid('page'),
      objects: Array.isArray(p.objects)
        ? p.objects
        : Array.isArray(p.strokes)
          ? p.strokes
          : [],
    }))
    if (!parsed.pages.some((p) => p.id === parsed.activePage)) {
      parsed.activePage = parsed.pages[0].id
    }
    return parsed
  } catch {
    return defaultDoc()
  }
}

function saveDoc(doc) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(doc))
  } catch {}
}

/** Schema comment helper for future chart bridge / validators. */
export function notesDocSchema() {
  // Intended shape — not a runtime validator (scaffold).
  return {
    version: 1,
    pages: [{ id: 'string', objects: ['StrokeObject'] }],
    activePage: 'string',
    // StrokeObject:
    // { type:'pen', id, color, width, points:[{x,y}], opacity? }
    // { type:'line', id, color, width, x1,y1,x2,y2 }
    // { type:'rect', id, color, width, x,y,w,h, fill:'none'|'hatch-blue'|'hatch-red' }
    // { type:'text', id, color, x,y, text, fontSize? }
    // { type:'chalkCandle', id, ... }  — see createChalkCandleStub
    // { type:'chalkFootprint', id, ... } — see createChalkFootprintStub
    // { type:'trendline'|'sr', id, ... } — mirror chart drawing store later
  }
}

/** TODO stub — FootprintChartCanvas can later push OHLC as a chalk candle stamp. */
export function createChalkCandleStub(ohlc = {}) {
  return {
    type: 'chalkCandle',
    id: uid('candle'),
    o: ohlc.o ?? ohlc.open ?? null,
    h: ohlc.h ?? ohlc.high ?? null,
    l: ohlc.l ?? ohlc.low ?? null,
    c: ohlc.c ?? ohlc.close ?? null,
    t: ohlc.t ?? ohlc.time ?? null,
    x: ohlc.x ?? 40,
    y: ohlc.y ?? 40,
    color: ohlc.color ?? CHALK.white,
  }
}

/** TODO stub — footprint cell grid stamp for Notes. */
export function createChalkFootprintStub(cells = {}) {
  return {
    type: 'chalkFootprint',
    id: uid('fp'),
    cells: cells.cells ?? cells ?? [],
    x: cells.x ?? 40,
    y: cells.y ?? 40,
    color: cells.color ?? CHALK.orange,
  }
}

function hatchPattern(ctx, color) {
  const size = 8
  const c = document.createElement('canvas')
  c.width = size
  c.height = size
  const g = c.getContext('2d')
  g.strokeStyle = color
  g.globalAlpha = 0.55
  g.lineWidth = 1
  g.beginPath()
  g.moveTo(0, size)
  g.lineTo(size, 0)
  g.stroke()
  return ctx.createPattern(c, 'repeat')
}

function drawObject(ctx, obj) {
  if (!obj) return
  const color = obj.color || CHALK.white
  ctx.save()
  ctx.strokeStyle = color
  ctx.fillStyle = color
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'

  if (obj.type === 'pen' && Array.isArray(obj.points) && obj.points.length > 1) {
    ctx.globalAlpha = obj.opacity ?? 0.88
    ctx.lineWidth = obj.width || 2.2
    ctx.beginPath()
    ctx.moveTo(obj.points[0].x, obj.points[0].y)
    for (let i = 1; i < obj.points.length; i++) {
      const p = obj.points[i]
      // slight jitter feel via quadratic midpoints
      const prev = obj.points[i - 1]
      const mx = (prev.x + p.x) / 2
      const my = (prev.y + p.y) / 2
      ctx.quadraticCurveTo(prev.x, prev.y, mx, my)
    }
    const last = obj.points[obj.points.length - 1]
    ctx.lineTo(last.x, last.y)
    ctx.stroke()
  } else if (obj.type === 'line') {
    ctx.globalAlpha = 0.9
    ctx.lineWidth = obj.width || 2
    ctx.beginPath()
    ctx.moveTo(obj.x1, obj.y1)
    ctx.lineTo(obj.x2, obj.y2)
    ctx.stroke()
  } else if (obj.type === 'rect') {
    ctx.globalAlpha = 0.92
    ctx.lineWidth = obj.width || 2
    const x = Math.min(obj.x, obj.x + obj.w)
    const y = Math.min(obj.y, obj.y + obj.h)
    const w = Math.abs(obj.w)
    const h = Math.abs(obj.h)
    if (obj.fill === 'hatch-blue' || obj.fill === 'hatch-red') {
      const hatchColor = obj.fill === 'hatch-red' ? CHALK.red : CHALK.blue
      ctx.fillStyle = hatchPattern(ctx, hatchColor)
      ctx.fillRect(x, y, w, h)
      ctx.strokeStyle = hatchColor
    }
    ctx.strokeRect(x + 0.5, y + 0.5, w, h)
  } else if (obj.type === 'text') {
    ctx.globalAlpha = 0.95
    ctx.font = `${obj.fontSize || 22}px 'Caveat', 'Segoe Script', 'Apple Chancery', 'Brush Script MT', cursive`
    ctx.fillText(obj.text || '', obj.x, obj.y)
  } else if (obj.type === 'chalkCandle') {
    // Minimal stub render — full chalk candle later
    ctx.globalAlpha = 0.85
    ctx.lineWidth = 1.5
    const cx = obj.x || 0
    const cy = obj.y || 0
    ctx.beginPath()
    ctx.moveTo(cx, cy - 18)
    ctx.lineTo(cx, cy + 18)
    ctx.stroke()
    ctx.strokeRect(cx - 6, cy - 8, 12, 16)
  } else if (obj.type === 'chalkFootprint') {
    ctx.globalAlpha = 0.8
    ctx.lineWidth = 1
    const x = obj.x || 0
    const y = obj.y || 0
    ctx.strokeRect(x, y, 48, 64)
    ctx.font = '10px Georgia, serif'
    ctx.fillText('FP', x + 4, y + 14)
  }
  // trendline / sr: reserved — no paint yet
  ctx.restore()
}


function distToSeg(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1
  const dy = y2 - y1
  const len2 = dx * dx + dy * dy
  if (len2 < 1e-6) return Math.hypot(px - x1, py - y1)
  let t = ((px - x1) * dx + (py - y1) * dy) / len2
  t = Math.max(0, Math.min(1, t))
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy))
}

function boundsForObject(obj) {
  if (!obj) return null
  if (obj.type === 'pen' && obj.points?.length) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    for (const pt of obj.points) {
      minX = Math.min(minX, pt.x); minY = Math.min(minY, pt.y)
      maxX = Math.max(maxX, pt.x); maxY = Math.max(maxY, pt.y)
    }
    const pad = (obj.width || 2) + 4
    return { x: minX - pad, y: minY - pad, w: maxX - minX + pad * 2, h: maxY - minY + pad * 2 }
  }
  if (obj.type === 'line') {
    const minX = Math.min(obj.x1, obj.x2), maxX = Math.max(obj.x1, obj.x2)
    const minY = Math.min(obj.y1, obj.y2), maxY = Math.max(obj.y1, obj.y2)
    return { x: minX - 6, y: minY - 6, w: maxX - minX + 12, h: maxY - minY + 12 }
  }
  if (obj.type === 'rect') {
    const x = Math.min(obj.x, obj.x + obj.w)
    const y = Math.min(obj.y, obj.y + obj.h)
    return { x, y, w: Math.abs(obj.w), h: Math.abs(obj.h) }
  }
  if (obj.type === 'text') {
    const fs = obj.fontSize || 22
    const w = Math.max(24, (obj.text || '').length * fs * 0.45)
    return { x: obj.x - 2, y: obj.y - fs, w: w + 4, h: fs + 8 }
  }
  if (obj.type === 'chalkCandle') {
    return { x: (obj.x || 0) - 10, y: (obj.y || 0) - 20, w: 20, h: 40 }
  }
  if (obj.type === 'chalkFootprint') {
    return { x: obj.x || 0, y: obj.y || 0, w: 48, h: 64 }
  }
  return null
}

function hitTestObject(obj, x, y) {
  if (!obj) return false
  if (obj.type === 'line') {
    return distToSeg(x, y, obj.x1, obj.y1, obj.x2, obj.y2) <= 8
  }
  if (obj.type === 'pen' && obj.points?.length) {
    for (let i = 1; i < obj.points.length; i++) {
      const a = obj.points[i - 1], b = obj.points[i]
      if (distToSeg(x, y, a.x, a.y, b.x, b.y) <= 8) return true
    }
    return false
  }
  const b = boundsForObject(obj)
  if (!b) return false
  return x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h
}

function hitTestTop(objects, x, y) {
  for (let i = objects.length - 1; i >= 0; i--) {
    if (hitTestObject(objects[i], x, y)) return objects[i]
  }
  return null
}

function translateObject(obj, dx, dy) {
  if (!obj || (!dx && !dy)) return obj
  if (obj.type === 'pen' && obj.points) {
    return { ...obj, points: obj.points.map((p) => ({ x: p.x + dx, y: p.y + dy })) }
  }
  if (obj.type === 'line') {
    return { ...obj, x1: obj.x1 + dx, y1: obj.y1 + dy, x2: obj.x2 + dx, y2: obj.y2 + dy }
  }
  if (obj.type === 'rect' || obj.type === 'text' || obj.type === 'chalkCandle' || obj.type === 'chalkFootprint') {
    return { ...obj, x: (obj.x || 0) + dx, y: (obj.y || 0) + dy }
  }
  return obj
}

function drawSelection(ctx, obj) {
  const b = boundsForObject(obj)
  if (!b) return
  ctx.save()
  ctx.strokeStyle = 'rgba(232, 228, 218, 0.55)'
  ctx.lineWidth = 1
  ctx.setLineDash([4, 3])
  ctx.strokeRect(b.x + 0.5, b.y + 0.5, b.w, b.h)
  ctx.setLineDash([])
  // corner ticks
  const s = 5
  ctx.beginPath()
  ctx.moveTo(b.x, b.y + s); ctx.lineTo(b.x, b.y); ctx.lineTo(b.x + s, b.y)
  ctx.moveTo(b.x + b.w - s, b.y); ctx.lineTo(b.x + b.w, b.y); ctx.lineTo(b.x + b.w, b.y + s)
  ctx.moveTo(b.x + b.w, b.y + b.h - s); ctx.lineTo(b.x + b.w, b.y + b.h); ctx.lineTo(b.x + b.w - s, b.y + b.h)
  ctx.moveTo(b.x + s, b.y + b.h); ctx.lineTo(b.x, b.y + b.h); ctx.lineTo(b.x, b.y + b.h - s)
  ctx.stroke()
  ctx.restore()
}

function paintBoard(ctx, w, h, objects, draft, selectedId) {
  ctx.clearRect(0, 0, w, h)
  // chalk-black + subtle noise via overlapping gradients
  ctx.fillStyle = CHALK.board
  ctx.fillRect(0, 0, w, h)
  const g = ctx.createLinearGradient(0, 0, w, h)
  g.addColorStop(0, 'rgba(255,255,255,0.015)')
  g.addColorStop(0.5, 'rgba(0,0,0,0.08)')
  g.addColorStop(1, 'rgba(255,255,255,0.02)')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, w, h)
  for (const obj of objects) drawObject(ctx, obj)
  if (draft) drawObject(ctx, draft)
  if (selectedId) {
    const sel = objects.find((o) => o.id === selectedId)
    if (sel) drawSelection(ctx, sel)
  }
}

const NotesPanel = forwardRef(function NotesPanel(_props, ref) {
  const [doc, setDoc] = useState(() => loadDoc())
  const [tool, setTool] = useState('pen')
  const [color, setColor] = useState(CHALK.white)
  const [rectFill, setRectFill] = useState('none') // none | hatch-blue | hatch-red
  const canvasRef = useRef(null)
  const wrapRef = useRef(null)
  const drawingRef = useRef(null) // in-progress object
  const [draft, setDraft] = useState(null)
  const [textEdit, setTextEdit] = useState(null) // { x, y, text, color } — inline caret, no prompt
  const textInputRef = useRef(null)
  const [selectedId, setSelectedId] = useState(null)
  const selectDragRef = useRef(null) // { id, lastX, lastY } while dragging selection
  const docRef = useRef(doc)
  docRef.current = doc
  const selectedIdRef = useRef(selectedId)
  selectedIdRef.current = selectedId

  const activePage = doc.pages.find((p) => p.id === doc.activePage) || doc.pages[0]
  const objects = activePage?.objects || []

  const persist = useCallback((next) => {
    setDoc(next)
    saveDoc(next)
  }, [])

  const updateActiveObjects = useCallback((updater) => {
    const cur = docRef.current
    const pages = cur.pages.map((p) => {
      if (p.id !== cur.activePage) return p
      const nextObjs = typeof updater === 'function' ? updater(p.objects || []) : updater
      return { ...p, objects: nextObjs }
    })
    persist({ ...cur, pages })
  }, [persist])

  const addObject = useCallback((obj) => {
    if (!obj || !obj.type) return null
    const withId = { ...obj, id: obj.id || uid(obj.type) }
    updateActiveObjects((objs) => [...objs, withId])
    return withId
  }, [updateActiveObjects])

  useImperativeHandle(ref, () => ({
    addObject,
    getDoc: () => docRef.current,
    createChalkCandleStub,
    createChalkFootprintStub,
  }), [addObject])

  // Resize canvas to wrap
  useEffect(() => {
    const wrap = wrapRef.current
    const canvas = canvasRef.current
    if (!wrap || !canvas) return
    const ro = new ResizeObserver(() => {
      const r = wrap.getBoundingClientRect()
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      const w = Math.max(1, Math.floor(r.width))
      const h = Math.max(1, Math.floor(r.height))
      canvas.width = Math.floor(w * dpr)
      canvas.height = Math.floor(h * dpr)
      canvas.style.width = `${w}px`
      canvas.style.height = `${h}px`
      const ctx = canvas.getContext('2d')
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      paintBoard(ctx, w, h, docRef.current.pages.find((p) => p.id === docRef.current.activePage)?.objects || [], drawingRef.current, selectedIdRef.current)
    })
    ro.observe(wrap)
    return () => ro.disconnect()
  }, [])

  // Warm the script face so canvas fillText matches the caret input
  useEffect(() => {
    try {
      if (document.fonts?.load) {
        document.fonts.load("22px Caveat").then(() => {
          const canvas = canvasRef.current
          const wrap = wrapRef.current
          if (!canvas || !wrap) return
          const r = wrap.getBoundingClientRect()
          const ctx = canvas.getContext('2d')
          const page = docRef.current.pages.find((p) => p.id === docRef.current.activePage)
          paintBoard(ctx, r.width, r.height, page?.objects || [], drawingRef.current, selectedIdRef.current)
        }).catch(() => {})
      }
    } catch {}
  }, [])

  // Repaint when objects / draft change
  useEffect(() => {
    const canvas = canvasRef.current
    const wrap = wrapRef.current
    if (!canvas || !wrap) return
    const r = wrap.getBoundingClientRect()
    const ctx = canvas.getContext('2d')
    paintBoard(ctx, r.width, r.height, objects, draft, selectedId)
  }, [objects, draft, doc.activePage, selectedId])

  const pointerPos = (e) => {
    const canvas = canvasRef.current
    const r = canvas.getBoundingClientRect()
    return { x: e.clientX - r.left, y: e.clientY - r.top }
  }

  const onPointerDown = (e) => {
    const canvas = canvasRef.current
    canvas.setPointerCapture?.(e.pointerId)
    const { x, y } = pointerPos(e)

    if (tool === 'select') {
      const page = docRef.current.pages.find((p) => p.id === docRef.current.activePage)
      const objs = page?.objects || []
      const hit = hitTestTop(objs, x, y)
      setSelectedId(hit ? hit.id : null)
      if (hit) {
        selectDragRef.current = { id: hit.id, lastX: x, lastY: y }
      } else {
        selectDragRef.current = null
      }
      return
    }

    setSelectedId(null)
    selectDragRef.current = null

    if (tool === 'text') {
      // Inline caret on the board — no browser prompt.
      setTextEdit({ x, y, text: '', color })
      return
    }

    if (tool === 'pen') {
      const obj = {
        type: 'pen',
        id: uid('pen'),
        color,
        width: 2.2 + Math.random() * 0.6,
        opacity: 0.82 + Math.random() * 0.12,
        points: [{ x, y }],
      }
      drawingRef.current = obj
      setDraft({ ...obj, points: [...obj.points] })
      return
    }

    if (tool === 'line') {
      const obj = { type: 'line', id: uid('line'), color, width: 2, x1: x, y1: y, x2: x, y2: y }
      drawingRef.current = obj
      setDraft({ ...obj })
      return
    }

    if (tool === 'rect') {
      const obj = {
        type: 'rect',
        id: uid('rect'),
        color,
        width: 2,
        x,
        y,
        w: 0,
        h: 0,
        fill: rectFill,
      }
      drawingRef.current = obj
      setDraft({ ...obj })
    }
  }

  const onPointerMove = (e) => {
    const drag = selectDragRef.current
    if (tool === 'select' && drag) {
      const { x, y } = pointerPos(e)
      const dx = x - drag.lastX
      const dy = y - drag.lastY
      drag.lastX = x
      drag.lastY = y
      if (dx || dy) {
        updateActiveObjects((objs) => objs.map((o) => (o.id === drag.id ? translateObject(o, dx, dy) : o)))
      }
      return
    }
    const cur = drawingRef.current
    if (!cur) return
    const { x, y } = pointerPos(e)
    if (cur.type === 'pen') {
      cur.points.push({ x, y })
      setDraft({ ...cur, points: [...cur.points] })
    } else if (cur.type === 'line') {
      cur.x2 = x
      cur.y2 = y
      setDraft({ ...cur })
    } else if (cur.type === 'rect') {
      cur.w = x - cur.x
      cur.h = y - cur.y
      setDraft({ ...cur })
    }
  }

  const onPointerUp = () => {
    if (selectDragRef.current) {
      selectDragRef.current = null
      return
    }
    const cur = drawingRef.current
    if (!cur) return
    drawingRef.current = null
    setDraft(null)
    if (cur.type === 'pen' && (!cur.points || cur.points.length < 2)) return
    if (cur.type === 'rect' && Math.abs(cur.w) < 3 && Math.abs(cur.h) < 3) return
    if (cur.type === 'line' && Math.hypot(cur.x2 - cur.x1, cur.y2 - cur.y1) < 3) return
    addObject(cur)
  }

  const commitTextEdit = () => {
    setTextEdit((cur) => {
      if (!cur) return null
      const trimmed = (cur.text || '').trim()
      if (trimmed) {
        addObject({ type: 'text', color: cur.color, x: cur.x, y: cur.y, text: trimmed, fontSize: 22 })
      }
      return null
    })
  }

  const cancelTextEdit = () => setTextEdit(null)

  // Focus the caret when text edit starts
  useEffect(() => {
    if (!textEdit) return
    const id = requestAnimationFrame(() => textInputRef.current?.focus())
    return () => cancelAnimationFrame(id)
  }, [textEdit])

  const deleteSelected = useCallback(() => {
    const id = selectedIdRef.current
    if (!id) return
    updateActiveObjects((objs) => objs.filter((o) => o.id !== id))
    setSelectedId(null)
  }, [updateActiveObjects])

  useEffect(() => {
    const onKey = (e) => {
      if (textEdit) return
      if (tool !== 'select' || !selectedIdRef.current) return
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault()
        deleteSelected()
      }
      if (e.key === 'Escape') setSelectedId(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [tool, textEdit, deleteSelected])

  const clearBoard = () => {
    if (!window.confirm('Clear this page?')) return
    setSelectedId(null)
    updateActiveObjects([])
  }

  const newPage = () => {
    const page = emptyPage()
    const cur = docRef.current
    persist({ ...cur, pages: [...cur.pages, page], activePage: page.id })
  }

  const pageIndex = doc.pages.findIndex((p) => p.id === doc.activePage)

  const switchPage = (offset) => {
    const cur = docRef.current
    const currentIndex = cur.pages.findIndex((p) => p.id === cur.activePage)
    const nextIndex = currentIndex + offset
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= cur.pages.length) return
    setSelectedId(null)
    persist({ ...cur, activePage: cur.pages[nextIndex].id })
  }

  return (
    <div className="notes-panel">
      <style>{`
        .notes-panel {
          height: 100%;
          min-height: 0;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          background: var(--surface, #0d0d0d);
        }
        .notes-panel-header {
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          height: var(--rail-h, 28px);
          padding: 0 var(--panel-pad, 8px);
          box-sizing: border-box;
          background: var(--surface-rail, #121212);
          border-bottom: 1px solid var(--seam, #222);
          border-top: 2px solid transparent;
          font-family: 'JetBrains Mono', monospace;
        }
        .notes-panel-title {
          color: var(--text-mid, #9a9a9a);
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }
        .notes-board {
          flex: 1 1 0;
          min-height: 0;
          display: flex;
          flex-direction: column;
          background: ${CHALK.board};
          position: relative;
        }
        .notes-toolbar {
          flex: 0 0 auto;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 6px;
          padding: 6px 8px;
          border-bottom: 1px solid rgba(232,228,218,0.12);
          font-family: Georgia, 'Times New Roman', serif;
          color: ${CHALK.white};
          background:
            linear-gradient(180deg, rgba(255,255,255,0.03), transparent),
            ${CHALK.board};
        }
        .notes-tool-btn, .notes-color-btn, .notes-action-btn {
          appearance: none;
          border: 1px solid rgba(232,228,218,0.28);
          background: transparent;
          color: ${CHALK.white};
          font: 12px/1.2 Georgia, serif;
          padding: 4px 8px;
          border-radius: 2px;
          cursor: pointer;
          opacity: 0.75;
        }
        .notes-tool-btn:hover, .notes-action-btn:hover { opacity: 1; }
        .notes-action-btn:disabled, .notes-action-btn:disabled:hover { opacity: 0.3; cursor: default; }
        .notes-tool-btn.active {
          opacity: 1;
          border-color: ${CHALK.orange};
          color: ${CHALK.orange};
          box-shadow: inset 0 0 0 1px rgba(224,137,58,0.35);
        }
        .notes-color-btn {
          width: 18px;
          height: 18px;
          padding: 0;
          border-radius: 50%;
          border-width: 2px;
        }
        .notes-color-btn.active { outline: 1px solid ${CHALK.white}; outline-offset: 2px; }
        .notes-canvas-wrap {
          flex: 1 1 0;
          min-height: 0;
          position: relative;
          cursor: crosshair;
        }
        .notes-canvas-wrap[data-tool="select"] { cursor: default; }
        .notes-canvas {
          display: block;
          width: 100%;
          height: 100%;
          touch-action: none;
        }
        .notes-page-meta {
          margin-left: auto;
          font-size: 11px;
          opacity: 0.55;
          letter-spacing: 0.04em;
        }
        .notes-fill-select {
          background: transparent;
          color: ${CHALK.white};
          border: 1px solid rgba(232,228,218,0.28);
          font: 11px Georgia, serif;
          padding: 3px 4px;
          opacity: 0.85;
        }
        .notes-text-input {
          position: absolute;
          z-index: 5;
          min-width: 8px;
          margin: 0;
          padding: 0;
          border: none;
          outline: none;
          background: transparent;
          font: 22px 'Caveat', 'Segoe Script', 'Apple Chancery', 'Brush Script MT', cursive;
          line-height: 1.2;
          caret-color: currentColor;
          color: inherit;
          box-shadow: none;
        }
      `}</style>

      <div className="notes-panel-header panel-rail">
        <span className="notes-panel-title">Notes</span>
      </div>

      <div className="notes-board">
        <div className="notes-toolbar">
          {TOOLS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`notes-tool-btn${tool === t.id ? ' active' : ''}`}
              onClick={() => { if (t.id !== 'text') setTextEdit(null); setTool(t.id) }}
              title={t.label}
            >
              {t.label}
            </button>
          ))}
          <span style={{ width: 8 }} />
          {COLORS.map((c) => (
            <button
              key={c.id}
              type="button"
              className={`notes-color-btn${color === c.hex ? ' active' : ''}`}
              style={{ background: c.hex, borderColor: c.hex }}
              onClick={() => setColor(c.hex)}
              title={c.id}
              aria-label={c.id}
            />
          ))}
          {tool === 'rect' && (
            <select
              className="notes-fill-select"
              value={rectFill}
              onChange={(e) => setRectFill(e.target.value)}
              title="Rect fill"
            >
              <option value="none">No hatch</option>
              <option value="hatch-blue">Blue hatch</option>
              <option value="hatch-red">Red hatch</option>
            </select>
          )}
          {tool === 'select' && selectedId && (
            <button type="button" className="notes-action-btn" onClick={deleteSelected} title="Delete selected (Del)">Delete</button>
          )}
          <button type="button" className="notes-action-btn" onClick={clearBoard}>Clear</button>
          <button type="button" className="notes-action-btn" onClick={newPage}>New page</button>
          <button
            type="button"
            className="notes-action-btn"
            onClick={() => switchPage(-1)}
            disabled={pageIndex <= 0}
            title="Previous page"
          >Prev</button>
          <button
            type="button"
            className="notes-action-btn"
            onClick={() => switchPage(1)}
            disabled={pageIndex < 0 || pageIndex >= doc.pages.length - 1}
            title="Next page"
          >Next</button>
          <span className="notes-page-meta">page {pageIndex + 1}/{doc.pages.length}</span>
        </div>

        <div
          className="notes-canvas-wrap"
          ref={wrapRef}
          data-tool={tool} style={{ cursor: tool === 'select' ? 'default' : undefined }}
        >
          <canvas
            ref={canvasRef}
            className="notes-canvas"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          />
          {textEdit && (
            <input
              ref={textInputRef}
              className="notes-text-input"
              style={{ left: textEdit.x, top: textEdit.y - 14, color: textEdit.color }}
              value={textEdit.text}
              spellCheck={false}
              autoComplete="off"
              onChange={(e) => setTextEdit((cur) => cur ? { ...cur, text: e.target.value } : cur)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); commitTextEdit() }
                else if (e.key === 'Escape') { e.preventDefault(); cancelTextEdit() }
              }}
              onBlur={() => commitTextEdit()}
            />
          )}
        </div>
      </div>
    </div>
  )
})

export default NotesPanel
